import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { blogAPI } from '../../services/api';
import { getStorageUrl } from '../../config/api';
import Header from '../Header';
import BottomNavigationBar from '../BottomNavigationBar';
import Footer from '../Footer';
import './BlogDetailPage.css';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await blogAPI.getBlog(slug);
      if (response.success && response.data) {
        setBlog(response.data);
      } else {
        setError(response.message || 'Blog post not found');
      }
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.message || 'Failed to load blog post. Please try again later.';
      setError(errorMessage);
      console.error('Error fetching blog:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (type) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (liking) {
      return; // Prevent multiple clicks
    }

    try {
      setLiking(true);
      const response = await blogAPI.toggleLike(slug, type);
      if (response.success && response.data) {
        // Update the blog state with new like/dislike counts and user like type
        setBlog(prevBlog => ({
          ...prevBlog,
          likes_count: response.data.likes_count !== undefined ? response.data.likes_count : prevBlog.likes_count,
          dislikes_count: response.data.dislikes_count !== undefined ? response.data.dislikes_count : prevBlog.dislikes_count,
          user_like_type: response.data.user_like_type
        }));
      } else {
        throw new Error(response.message || 'Failed to update like');
      }
    } catch (err) {
      console.error('Error toggling like:', err);
      const errorMessage = err.message || err.response?.data?.message || 'Failed to update like. Please try again.';
      alert(errorMessage);
    } finally {
      setLiking(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!commentText.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await blogAPI.addComment(slug, commentText, null);
      if (response.success) {
        setCommentText('');
        fetchBlog(); // Refresh to get updated comments
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      alert(err.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplySubmit = async (parentId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!replyText.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await blogAPI.addComment(slug, replyText, parentId);
      if (response.success) {
        setReplyText('');
        setReplyingTo(null);
        fetchBlog(); // Refresh to get updated comments
      }
    } catch (err) {
      console.error('Error adding reply:', err);
      alert(err.message || 'Failed to add reply');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleCategoriesClick = () => {
    navigate('/categories');
  };

  const handleHowItWorksClick = () => {
    navigate('/#how-it-works');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleAccountSettingsClick = () => {
    navigate('/settings');
  };

  const handleForumClick = () => {
    navigate('/forum');
  };

  const handleBlogClick = () => {
    navigate('/blog');
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleJobsClick = () => {
    navigate('/vacancies');
  };

  if (authLoading || loading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="App blog-detail-wrapper">
        <div className="blog-header">
          <Header 
            onLoginClick={handleLoginClick}
            onSignupClick={handleSignupClick}
            isLoggedIn={isAuthenticated}
            user={user}
            onHomeClick={handleHomeClick}
            onCategoriesClick={handleCategoriesClick}
            onHowItWorksClick={handleHowItWorksClick}
            onProfileClick={handleProfileClick}
            onAccountSettingsClick={handleAccountSettingsClick}
            onForumClick={handleForumClick}
            currentPage="blog"
          />
        </div>
        <div className="blog-detail-container">
          <div className="blog-error">
            <p>{error || 'Blog post not found'}</p>
            <Link to="/blog" className="blog-back-link">← Back to Blog</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App blog-detail-wrapper">
      <div className="blog-header">
        <Header 
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={handleHomeClick}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onForumClick={handleForumClick}
          currentPage="blog"
        />
      </div>

      <div className="blog-detail-content">
        <div className="blog-detail-container">
          <Link to="/blog" className="blog-back-link">← Back to Blog</Link>

          {error && !blog && (
            <div className="blog-error">
              <p>{error}</p>
              <Link to="/blog" className="blog-back-link">← Back to Blog</Link>
            </div>
          )}

          {blog && (
          <article className="blog-detail-article">
            {blog.featured_image && (
              <div className="blog-detail-image">
                <img 
                  src={getStorageUrl(blog.featured_image)} 
                  alt={blog.title}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            <header className="blog-detail-header">
              <h1 className="blog-detail-title">{blog.title}</h1>
              <div className="blog-detail-meta">
                <span className="blog-detail-date">{formatDate(blog.created_at)}</span>
                <div className="blog-detail-stats">
                  <span className="blog-stat">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    {blog.views_count || 0} views
                  </span>
                </div>
              </div>
            </header>

            <div 
              className="blog-detail-content-text"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />

            {/* Like/Dislike Section */}
            <div className="blog-detail-actions">
              <button
                onClick={() => handleLike('like')}
                className={`blog-action-btn ${blog.user_like_type === 'like' ? 'active' : ''}`}
                disabled={!isAuthenticated || liking}
                title={!isAuthenticated ? 'Login to like' : liking ? 'Processing...' : ''}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 9V5a3 3 0 0 0-6 0v4"></path>
                  <path d="M10 11v6a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-6a2 2 0 0 0-2 2z"></path>
                </svg>
                Like ({blog.likes_count || 0})
              </button>
              <button
                onClick={() => handleLike('dislike')}
                className={`blog-action-btn ${blog.user_like_type === 'dislike' ? 'active' : ''}`}
                disabled={!isAuthenticated || liking}
                title={!isAuthenticated ? 'Login to dislike' : liking ? 'Processing...' : ''}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13v6a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-6a2 2 0 0 0-2 2z"></path>
                  <path d="M14 9V5a3 3 0 0 0-6 0v4"></path>
                </svg>
                Dislike ({blog.dislikes_count || 0})
              </button>
            </div>

            {/* Comments Section */}
            <div className="blog-detail-comments">
              <h2 className="blog-comments-title">
                Comments ({blog.comments_count || 0})
              </h2>

              {/* Comment Form */}
              {isAuthenticated ? (
                <form onSubmit={handleCommentSubmit} className="blog-comment-form">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Write a comment..."
                    rows="4"
                    className="blog-comment-input"
                    required
                  />
                  <button 
                    type="submit" 
                    className="blog-comment-submit"
                    disabled={submitting || !commentText.trim()}
                  >
                    {submitting ? 'Posting...' : 'Post Comment'}
                  </button>
                </form>
              ) : (
                <div className="blog-comment-login-prompt">
                  <p>Please <Link to="/login">login</Link> to comment.</p>
                </div>
              )}

              {/* Comments List */}
              <div className="blog-comments-list">
                {blog.comments && blog.comments.length > 0 ? (
                  blog.comments.map((comment) => (
                    <div key={comment.id} className="blog-comment">
                      <div className="blog-comment-header">
                        <span className="blog-comment-author">{comment.user?.name || 'Anonymous'}</span>
                        <span className="blog-comment-date">{formatDate(comment.created_at)}</span>
                      </div>
                      <div className="blog-comment-text">{comment.comment}</div>
                      
                      {/* Reply Button */}
                      {isAuthenticated && (
                        <button
                          onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                          className="blog-comment-reply-btn"
                        >
                          {replyingTo === comment.id ? 'Cancel' : 'Reply'}
                        </button>
                      )}

                      {/* Reply Form */}
                      {replyingTo === comment.id && isAuthenticated && (
                        <form 
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleReplySubmit(comment.id);
                          }}
                          className="blog-reply-form"
                        >
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write a reply..."
                            rows="3"
                            className="blog-comment-input"
                            required
                          />
                          <button 
                            type="submit" 
                            className="blog-comment-submit"
                            disabled={submitting || !replyText.trim()}
                          >
                            {submitting ? 'Posting...' : 'Post Reply'}
                          </button>
                        </form>
                      )}

                      {/* Replies */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div className="blog-comment-replies">
                          {comment.replies.map((reply) => (
                            <div key={reply.id} className="blog-comment blog-comment-reply">
                              <div className="blog-comment-header">
                                <span className="blog-comment-author">{reply.user?.name || 'Anonymous'}</span>
                                <span className="blog-comment-date">{formatDate(reply.created_at)}</span>
                              </div>
                              <div className="blog-comment-text">{reply.comment}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="blog-no-comments">No comments yet. Be the first to comment!</p>
                )}
              </div>
            </div>
          </article>
          )}
        </div>
      </div>

      <Footer />
      
      <BottomNavigationBar
        onHomeClick={handleHomeClick}
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onJobsClick={handleJobsClick}
        onForumClick={handleForumClick}
      />
    </div>
  );
};

export default BlogDetailPage;
